import React, { useEffect, useRef, useState } from 'react'
import {
  Button,
  ConfigProvider,
  Modal,
  notification,
  Select,
  Space,
  Switch,
  Table,
  Tooltip
} from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import {
  deleteCheckSuggestion,
  findCheckSuggestions,
  updateCheckSuggestion
} from '../../../common/request'
import ResponseCode from '../../../common/response-code'
import Styles from './ConfigCheckSuggestion.module.scss'
import CheckSuggestionModal from './CheckSuggestionModal'
import {
  getDeleteConfirmContent,
  STATUS_FILTER_OPTIONS
} from './checkSuggestionConstants'

/** 机构管理-机构配置-审核建议 */
function ConfigCheckSuggestion({ orgItem, modalVisible }) {
  const modalRef = useRef()
  const [tableData, setTableData] = useState([])
  const [loading, setLoading] = useState(false)
  const [statusFilter, setStatusFilter] = useState('')

  useEffect(() => {
    if (orgItem?.id && modalVisible) getTableData()
  }, [orgItem, modalVisible])

  const errorModal = (content) => {
    Modal.error({ title: '错误', content })
  }

  const getTableData = async () => {
    if (!orgItem?.id) return
    setLoading(true)
    try {
      const params = { orgId: orgItem.id }
      if (statusFilter !== '' && statusFilter != null) {
        params.status = Number(statusFilter)
      }
      const res = await findCheckSuggestions(params)
      setLoading(false)
      if (res.code !== ResponseCode.success) return errorModal(res.msg)
      setTableData(Array.isArray(res.data) ? res.data : [])
    } catch (error) {
      setLoading(false)
      console.error(error)
      errorModal('网络错误，查询列表失败')
    }
  }

  const openModal = (type, data) => {
    modalRef.current.showModal(type, data)
  }

  const deleteConfirm = (item) => {
    Modal.confirm({
      title: '删除审核建议',
      content: getDeleteConfirmContent(item.title),
      onOk: () => deleteSuggestion(item.id)
    })
  }

  const deleteSuggestion = async (id) => {
    setLoading(true)
    try {
      const res = await deleteCheckSuggestion({ orgId: orgItem.id, id })
      setLoading(false)
      if (res.code !== ResponseCode.success) return errorModal(res.msg)
      notification.success({ message: '操作成功' })
      getTableData()
    } catch (error) {
      setLoading(false)
      console.error(error)
      errorModal('网络错误，删除失败')
    }
  }

  const handleChangeStatus = async (record) => {
    const nextStatus = record.status === 1 ? 0 : 1
    setLoading(true)
    try {
      const res = await updateCheckSuggestion({
        orgId: orgItem.id,
        id: record.id,
        title: record.title,
        suggestion: record.suggestion,
        status: nextStatus
      })
      setLoading(false)
      if (res.code !== ResponseCode.success) return errorModal(res.msg)
      notification.success({ message: '操作成功' })
      getTableData()
    } catch (error) {
      setLoading(false)
      console.error(error)
      errorModal('网络错误，操作失败')
    }
  }

  const operationBtnList = [
    { label: '编辑', fn: (row) => openModal('update', row) },
    { label: '删除', fn: (row) => deleteConfirm(row) }
  ]

  const columns = [
    {
      title: '建议标题',
      dataIndex: 'title',
      width: 140,
      ellipsis: true,
      render: (text) => (
        <Tooltip title={text}>
          <span>{text}</span>
        </Tooltip>
      )
    },
    {
      title: '建议内容',
      dataIndex: 'suggestion',
      ellipsis: true,
      render: (text) => (
        <Tooltip title={text}>
          <span>{text}</span>
        </Tooltip>
      )
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      render: (status, record) => (
        <Switch checked={status === 1} onChange={() => handleChangeStatus(record)} />
      )
    },
    {
      title: '操作',
      dataIndex: 'operation',
      width: 160,
      render: (_, row) => (
        <Space className="d-flex justify-content-end">
          {operationBtnList.map((item) => (
            <Button
              type="primary"
              ghost
              danger={item.label === '删除'}
              onClick={() => item.fn(row)}
              key={item.label}
            >
              {item.label}
            </Button>
          ))}
        </Space>
      )
    }
  ]

  return (
    <>
      <div className={Styles.headerBar}>
        <div>
          <Select
            className={Styles.searcher}
            value={statusFilter}
            options={STATUS_FILTER_OPTIONS}
            onChange={setStatusFilter}
          />
          <Button type="primary" onClick={getTableData}>
            查询
          </Button>
        </div>

        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => openModal('insert')}
        >
          新增审核建议
        </Button>
      </div>

      <ConfigProvider componentSize="small">
        <Table
          columns={columns}
          dataSource={tableData}
          rowKey={(record) => record.id}
          loading={loading}
          pagination={true}
        />
      </ConfigProvider>

      <CheckSuggestionModal
        ref={modalRef}
        orgId={orgItem?.id}
        onSuccess={getTableData}
      />
    </>
  )
}

export default ConfigCheckSuggestion
